// ============================================
// RESOURCE TYPES
// ============================================

export interface CaseStudy {
  id: string;
  slug: string;
  title: string;
  company: string;
  industry: string;
  excerpt: string;
  imageUrl: string;
  results: {
    label: string;
    value: string;
  }[];
  href: string;
  challenge: string;
  solution: string;
  detailedResults: string;
  quote: string;
  quotePerson: string;
  quoteRole: string;
  companyDescription: string;
  companyStats?: {
    employees?: string;
    location?: string;
    founded?: string;
    revenue?: string;
  };
}

export interface Guide {
  id: string;
  title: string;
  description: string;
  category: 'growth' | 'operations' | 'technology' | 'leadership';
  imageUrl: string;
  downloadUrl: string;
  pageCount: number;
}

export interface Webinar {
  id: string;
  title: string;
  description: string;
  speaker: string;
  speakerRole: string;
  speakerImage: string;
  duration: string;
  date: string;
  imageUrl: string;
  registrationUrl: string;
  isUpcoming: boolean;
}

// ============================================
// CASE STUDIES DATA
// ============================================

export const caseStudies: CaseStudy[] = [
  {
    id: 'comfort-first-hvac',
    slug: 'comfort-first-hvac',
    title: 'How Comfort First HVAC Increased Revenue by 47% in 6 Months',
    company: 'Comfort First HVAC',
    industry: 'HVAC',
    excerpt:
      'After struggling with 5 different software tools, Comfort First consolidated everything into Omniforge and saw dramatic improvements in efficiency and revenue.',
    imageUrl: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&h=400&fit=crop',
    results: [
      { label: 'Revenue Increase', value: '+47%' },
      { label: 'Response Time', value: '-65%' },
      { label: 'Customer Satisfaction', value: '98%' },
      { label: 'First-Time Fix Rate', value: '94%' },
    ],
    href: '/case-study/comfort-first-hvac',
    challenge: `Comfort First HVAC was growing fast, but their operations couldn't keep up. The team was using five different software tools: one for scheduling, another for invoicing, a third for customer management, a fourth for inventory, and spreadsheets to tie it all together.

The result was chaos. Technicians often showed up to jobs without the right parts because inventory wasn't synced with scheduling. Customer service reps couldn't answer basic questions without switching between three different screens. And the owner, Mike Patterson, was spending 15+ hours every week just reconciling data between systems.

"We were literally drowning in administrative work," Mike recalls. "I started this business because I love HVAC, not data entry. But I was spending more time in front of a computer than I was helping customers."

The breaking point came when they double-booked a major commercial installation with three residential service calls. The resulting customer complaints and rescheduling nightmare cost them over $12,000 in lost revenue and refunds.`,
    solution: `Omniforge gave Comfort First a single platform that replaced all five of their existing tools. The implementation took just three weeks, including data migration from their old systems and training for all 23 employees.

Key features that made the difference:

**Unified Dispatch Board**: All jobs, technician locations, and availability visible on one screen. The drag-and-drop interface made rescheduling effortless, and automated conflict detection prevented double-bookings.

**Real-Time Inventory Management**: Parts levels sync automatically with job assignments. Technicians see exactly what's in their truck and what needs to be ordered. The system even predicts seasonal demand and suggests reorder quantities.

**Integrated Customer Portal**: Customers can book appointments, track technician arrival times, review estimates, and pay invoices all in one place. This reduced inbound calls by 40% and virtually eliminated payment delays.

**Mobile-First Technician App**: Techs have everything they need on their phones - job details, customer history, equipment specs, and even troubleshooting guides. They can capture signatures, process payments, and create follow-up appointments on the spot.

"The training was surprisingly easy," says Operations Manager Sarah Chen. "Within a week, even our most tech-resistant techs were using the app like pros."`,
    detailedResults: `Within six months of implementing Omniforge, Comfort First HVAC achieved remarkable results across every aspect of their business:

**Revenue Growth: +47%**
With streamlined operations, Comfort First was able to complete 35% more jobs per day. The improved customer experience also led to a 28% increase in maintenance agreement sign-ups, creating predictable recurring revenue.

**Average Response Time: -65%**
Automated scheduling and real-time technician tracking allowed them to respond to emergency calls in under 2 hours on average, down from 6+ hours previously. This dramatically improved their reputation for reliability.

**Customer Satisfaction: 98%**
The combination of faster response times, first-time fixes, and easy payment options pushed their NPS score from 42 to 78. Online reviews improved from 3.8 stars to 4.9 stars on Google.

**First-Time Fix Rate: 94%**
With full access to customer equipment history and real-time inventory visibility, technicians arrive prepared. Parts shortages dropped by 82%, and callbacks fell by 67%.

**Administrative Hours: -75%**
Mike now spends just 4 hours per week on admin tasks instead of 15+. The automated invoicing, payment processing, and reporting features handle what used to require manual effort.

**Employee Satisfaction: +40%**
In an industry with notoriously high turnover, Comfort First has retained 100% of their technicians since implementing Omniforge. Techs report that the easy-to-use mobile app and elimination of paperwork makes their jobs significantly more enjoyable.`,
    quote: "Omniforge didn't just save us time - it transformed how we operate. We went from constantly putting out fires to actually growing our business strategically. I finally feel like I'm running the company instead of the company running me.",
    quotePerson: 'Mike Patterson',
    quoteRole: 'Owner & CEO',
    companyDescription: `Comfort First HVAC is a full-service heating, ventilation, and air conditioning company serving the greater Phoenix metropolitan area. Founded in 2015, they've grown from a two-person operation to a team of 23 technicians and 8 office staff, serving both residential and commercial clients.

Their services include HVAC installation, repair, and maintenance, as well as indoor air quality solutions and smart thermostat installation. They're particularly known for their rapid emergency response and commitment to customer education.

Comfort First has been recognized as one of Phoenix's fastest-growing small businesses for three consecutive years and maintains an A+ rating with the Better Business Bureau.`,
    companyStats: {
      employees: '31',
      location: 'Phoenix, AZ',
      founded: '2015',
      revenue: '$4.2M ARR',
    },
  },
  {
    id: 'precision-plumbing',
    slug: 'precision-plumbing',
    title: 'Precision Plumbing Saves 20 Hours Per Week on Admin',
    company: 'Precision Plumbing Co.',
    industry: 'Plumbing',
    excerpt:
      'Manual scheduling and paper invoices were killing productivity. See how Precision Plumbing automated their entire operation.',
    imageUrl: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&h=400&fit=crop',
    results: [
      { label: 'Hours Saved Weekly', value: '20+' },
      { label: 'Invoice Processing', value: '-80%' },
      { label: 'Jobs Per Day', value: '+35%' },
      { label: 'Cash Flow Improvement', value: '+$45K/mo' },
    ],
    href: '/case-study/precision-plumbing',
    challenge: `Precision Plumbing had been in business for 18 years, and for most of that time, paper was king. Work orders were written by hand. Invoices were typed up at the end of each day. Scheduling was done on a giant whiteboard in the office.

"My dad started this company, and that's how he always did it," explains current owner Jennifer Reyes. "When I took over five years ago, I knew we needed to modernize, but I didn't know where to start."

The paper-based system created massive inefficiencies:
- Technicians would return to the office at 5 PM with a stack of work orders that office staff then had to manually enter into QuickBooks
- Invoices often went out 3-5 days after job completion, delaying payment by weeks
- The scheduling whiteboard was constantly being photographed and texted to techs, who often had outdated information
- Customer history was stored in filing cabinets, making it nearly impossible to reference past work during service calls

The biggest pain point was cash flow. With delayed invoicing and manual payment tracking, Precision Plumbing's average days-to-payment was 47 days. They were essentially financing their customers' plumbing work for over a month.

"We had the work. We had great technicians. But we were constantly stressed about cash flow because our admin processes were so slow," Jennifer says.`,
    solution: `Omniforge replaced Precision Plumbing's paper-based workflow with a completely digital operation. The transition was carefully planned to minimize disruption to ongoing jobs.

**Phase 1: Digital Scheduling (Week 1-2)**
The whiteboard was retired and replaced with Omniforge's dispatch board. Historical customer data was imported from their old systems, and technicians were equipped with tablets for the mobile app.

**Phase 2: Field Operations (Week 3-4)**
Technicians began using the mobile app for everything: clocking in, viewing job details, capturing before/after photos, getting customer signatures, and processing payments on-site. Paper work orders were eliminated entirely.

**Phase 3: Financial Integration (Week 5-6)**
Omniforge was connected to their accounting software, enabling automatic invoice generation and payment tracking. Follow-up reminders were automated, and online payment options were activated.

Key features that transformed their operations:

**Instant Invoicing**: Invoices are generated and sent the moment a tech marks a job complete. Customers can pay immediately via credit card, ACH, or financing options.

**GPS-Enabled Scheduling**: The system considers technician locations, traffic patterns, and job requirements when suggesting optimal scheduling. This eliminated the constant phone calls and text messages about "where to go next."

**Photo Documentation**: Every job includes timestamped photos that are automatically attached to customer records. This has proven invaluable for warranty claims and customer disputes.

**Automated Follow-Ups**: The system automatically sends appointment reminders, payment reminders, and maintenance check-in messages, reducing office staff workload dramatically.`,
    detailedResults: `The transformation at Precision Plumbing was swift and dramatic. Here's what changed in the first 90 days:

**Administrative Time: -20+ Hours/Week**
Office staff went from spending 6+ hours daily on data entry, invoicing, and schedule coordination to under 2 hours. The two-person office team now handles what previously required three people.

**Invoice Processing Time: -80%**
Invoices that used to take 3-5 days to reach customers now arrive within minutes of job completion. The average invoice is paid within 8 days, down from 47 days.

**Jobs Completed Daily: +35%**
With optimized routing and elimination of return-to-office requirements, each technician completes an average of 1.5 more jobs per day. For a team of 12 techs, that's 18 additional jobs daily.

**Monthly Cash Flow: +$45,000**
The combination of faster invoicing, on-site payment options, and automated follow-ups improved cash flow by $45,000 per month. The business went from constantly managing cash crunches to building a healthy reserve.

**Customer Response Rate: 94%**
Automated appointment reminders reduced no-shows from 12% to under 3%. Customers appreciate the professionalism and modern communication.

**Technician Productivity: +28%**
With everything they need on their tablets, techs spend less time on the phone with the office and more time doing what they do best - fixing plumbing. Overtime decreased by 40% while job completion increased.

**Annual Revenue Impact: +$380,000**
When combining all efficiency gains, Precision Plumbing projects an annual revenue increase of $380,000 with no additional hiring.`,
    quote: "I used to joke that I was running a paper company that happened to do plumbing. Now we're finally a plumbing company that happens to have great software. My dad would be amazed at how smooth everything runs.",
    quotePerson: 'Jennifer Reyes',
    quoteRole: 'Owner & President',
    companyDescription: `Precision Plumbing Co. is a family-owned plumbing contractor serving Denver, Colorado and surrounding communities. Founded in 2006 by Master Plumber Carlos Reyes, the company has earned a reputation for quality workmanship and honest pricing.

Now led by Carlos's daughter Jennifer, Precision Plumbing handles everything from simple drain cleaning to complete home repiping projects. They specialize in both residential and light commercial plumbing, with particular expertise in older home renovations and modern fixture installation.

The company is a member of the Plumbing-Heating-Cooling Contractors Association (PHCC) and employs only licensed, background-checked technicians. They've been voted "Best Plumber" by Denver Westword readers for four consecutive years.`,
    companyStats: {
      employees: '16',
      location: 'Denver, CO',
      founded: '2006',
      revenue: '$2.8M ARR',
    },
  },
  {
    id: 'greenscape-landscaping',
    slug: 'greenscape-landscaping',
    title: 'GreenScape Landscaping Scales from 5 to 25 Trucks',
    company: 'GreenScape Landscaping',
    industry: 'Landscaping',
    excerpt:
      'With route optimization and real-time crew tracking, GreenScape was able to scale rapidly without adding overhead.',
    imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
    results: [
      { label: 'Fleet Growth', value: '5x' },
      { label: 'Fuel Costs', value: '-28%' },
      { label: 'Crew Efficiency', value: '+45%' },
      { label: 'Customer Retention', value: '96%' },
    ],
    href: '/case-study/greenscape-landscaping',
    challenge: `GreenScape Landscaping had a problem that most business owners would envy: too much demand. Their quality work and competitive pricing had generated a waiting list of potential customers. But owner Marcus Thompson couldn't figure out how to scale without the business falling apart.

"Every time we tried to grow, things got worse," Marcus recalls. "We'd add a truck and a crew, and suddenly we'd have missed appointments, customer complaints, and crews sitting idle while other crews were overbooked."

The core issues were coordination and visibility:

**Route Chaos**: With 5 trucks serving 400+ properties, routes were planned manually each week. This took Marcus 6-8 hours every Sunday, and the routes were rarely optimal. Crews often drove past properties they'd service later in the day.

**Communication Breakdown**: There was no efficient way to communicate schedule changes. If a crew finished early or a customer canceled, the office had no way to quickly reassign work. Crews would call in and wait for instructions while billable hours ticked away.

**Quality Inconsistency**: Without a way to track what was done at each property, quality varied wildly. Some properties were being serviced twice while others were skipped. Customer complaints increased 40% in six months.

**Billing Nightmares**: Tracking which services were provided to which properties was a nightmare. They were losing an estimated $3,000-5,000 monthly to unbilled services and incorrect invoices.

Marcus had tried adding supervisors and coordinators, but the overhead ate into profits without solving the underlying visibility problem.`,
    solution: `Omniforge provided GreenScape with the operational infrastructure they needed to scale efficiently. The implementation focused on three core areas:

**Intelligent Route Optimization**
Omniforge analyzes property locations, service requirements, crew capabilities, and real-time traffic to create optimal daily routes. What used to take Marcus 8 hours now happens automatically overnight.

The system considers factors human planners often miss:
- Grouping properties by service type to minimize equipment changes
- Accounting for crew skill levels and certifications
- Balancing workload across crews for consistent finish times
- Adjusting routes in real-time for weather, cancellations, or emergencies

**Real-Time Crew Tracking**
GPS-enabled tracking shows exactly where every truck is and what job they're on. The dispatch board updates in real-time, allowing office staff to make informed decisions instantly.

When a crew finishes early, the system automatically suggests nearby properties that could use attention. When a customer cancels, affected crews are notified immediately with updated routes.

**Digital Service Verification**
Crews use the mobile app to check in at properties, log completed services, and capture photos. This creates an audit trail that prevents missed services and provides documentation for billing.

The property history feature shows crews exactly what was done on previous visits, ensuring consistent service quality regardless of which crew is assigned.

**Automated Customer Communication**
Customers receive automatic notifications when their crew is en route, including estimated arrival time. Post-service reports with photos are sent automatically, demonstrating the value of the work performed.`,
    detailedResults: `Over 18 months, GreenScape transformed from a struggling 5-truck operation to a well-oiled 25-truck enterprise. Here's the detailed breakdown:

**Fleet Growth: 5x (5 to 25 trucks)**
With operational efficiency solved, GreenScape could scale confidently. They added crews strategically, with each new truck becoming productive within 2 weeks. The business grew from $1.2M to $5.4M in annual revenue.

**Fuel Cost Reduction: -28%**
Optimized routing reduced average daily mileage per truck from 67 miles to 48 miles. With fuel prices volatile, this translates to savings of approximately $2,400 per truck per year, or $60,000 annually across the fleet.

**Crew Efficiency: +45%**
Crews now complete an average of 18 properties per day, up from 12.4. The combination of better routes, eliminated wait time, and streamlined check-in/check-out processes makes every minute count.

**Customer Retention: 96%**
Consistent service quality and proactive communication dramatically improved retention. The automatic photo documentation particularly impressed customers, who now have visual proof of every service.

**Revenue Recovery: +$4,200/month**
Digital service tracking eliminated the billing leakage that was costing thousands monthly. Every service is logged, verified, and billed accurately.

**Management Time: -15 hours/week**
Marcus no longer spends Sundays planning routes or weekdays firefighting scheduling issues. He's redirected that time to business development, landing two major commercial contracts worth $400K annually.

**Scalable Operations**: The same office team of 3 people that managed 5 trucks now manages 25 trucks. Omniforge's automation prevented the need for proportional staff increases.`,
    quote: "Before Omniforge, I thought scaling was impossible without hiring a fleet of managers. Now I realize I just needed better systems. We've 5x'd our business with the same office team, and honestly, their jobs are easier now than when we had 5 trucks.",
    quotePerson: 'Marcus Thompson',
    quoteRole: 'Founder & CEO',
    companyDescription: `GreenScape Landscaping is a full-service commercial and residential landscaping company based in Austin, Texas. Founded in 2017, the company has grown from a solo operation with a pickup truck to one of Central Texas's largest independent landscaping contractors.

Services include lawn maintenance, landscape design and installation, irrigation system management, seasonal color programs, and commercial property management. GreenScape is particularly known for their sustainable landscaping practices, including native plant specialization and water-efficient irrigation design.

The company maintains long-term contracts with over 2,000 residential properties and 85 commercial accounts, including several major apartment complexes and corporate campuses. They've been featured in Austin Business Journal's "Fast 50" list of fastest-growing private companies.`,
    companyStats: {
      employees: '58',
      location: 'Austin, TX',
      founded: '2017',
      revenue: '$5.4M ARR',
    },
  },
];

// ============================================
// GUIDES & EBOOKS DATA
// ============================================

export const guides: Guide[] = [
  {
    id: 'scaling-service-business',
    title: 'The Complete Guide to Scaling Your Service Business',
    description:
      'Learn the proven strategies top service companies use to grow from $1M to $10M in revenue. Covers hiring, systems, and technology.',
    category: 'growth',
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
    downloadUrl: '/resources/guides/scaling-service-business',
    pageCount: 45,
  },
  {
    id: 'dispatch-efficiency',
    title: 'Dispatch Efficiency: From Chaos to Control',
    description:
      'Master the art of smart scheduling, route optimization, and real-time crew management to maximize your daily job capacity.',
    category: 'operations',
    imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop',
    downloadUrl: '/resources/guides/dispatch-efficiency',
    pageCount: 32,
  },
  {
    id: 'digital-transformation',
    title: 'Digital Transformation for Trades Companies',
    description:
      'A practical roadmap for moving from paper and spreadsheets to a fully digital operation without disrupting your team.',
    category: 'technology',
    imageUrl: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop',
    downloadUrl: '/resources/guides/digital-transformation',
    pageCount: 28,
  },
];

// ============================================
// WEBINARS DATA
// ============================================

export const webinars: Webinar[] = [
  {
    id: 'maximizing-technician-productivity',
    title: 'Maximizing Technician Productivity in 2026',
    description:
      'Learn how to get more jobs done per day without burning out your team. Covers scheduling, mobile tools, and incentive structures.',
    speaker: 'Marcus Johnson',
    speakerRole: 'VP of Operations, Omniforge',
    speakerImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    duration: '45 min',
    date: '2026-02-15',
    imageUrl: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=600&h=400&fit=crop',
    registrationUrl: '/resources/webinars/technician-productivity',
    isUpcoming: true,
  },
  {
    id: 'customer-retention-strategies',
    title: 'Customer Retention Strategies That Actually Work',
    description:
      'Discover how leading service companies achieve 80%+ retention rates through proactive communication and maintenance programs.',
    speaker: 'Sarah Chen',
    speakerRole: 'Customer Success Director, Omniforge',
    speakerImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    duration: '50 min',
    date: '2026-01-20',
    imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
    registrationUrl: '/resources/webinars/customer-retention',
    isUpcoming: false,
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getCaseStudyById(id: string): CaseStudy | undefined {
  return caseStudies.find((cs) => cs.id === id);
}

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return caseStudies.find((cs) => cs.slug === slug);
}

export function getRelatedCaseStudies(currentSlug: string, limit: number = 2): CaseStudy[] {
  return caseStudies.filter((cs) => cs.slug !== currentSlug).slice(0, limit);
}

export function getGuidesByCategory(category: Guide['category']): Guide[] {
  return guides.filter((g) => g.category === category);
}

export function getUpcomingWebinars(): Webinar[] {
  return webinars.filter((w) => w.isUpcoming);
}

export function getPastWebinars(): Webinar[] {
  return webinars.filter((w) => !w.isUpcoming);
}
